import { PlusIcon } from '@heroicons/react/24/outline';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import FieldsetContainer from '@/components/shared/FieldsetContainer';
import PedWorkspaceGuard from '@/components/shared/PedWorkspaceGuard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Button, Loading } from '@/components/ui/daisy';
import useFormList from '@/data/hook/shared/useFormList';
import { ActivityBlockType } from '@/models/Activity';
import ActivityBlockInlineForm from './ActivityBlockInlineForm';
import { usePedWorkspaceStatus } from '@/data/context/PedWorkspaceContext';

type CreateBlockData = {
  name: string;
  activityType: ActivityBlockType;
};

type ActivityBlockFormListProps = {
  onSubmit: (data: CreateBlockData) => void;
  isLoading: boolean;
  addBlockName?: string;
  showBlockTypeSelector?: boolean;
  actionsAddon?: ReactNode;
};

export default function ActivityBlockFormList({
  onSubmit,
  isLoading,
  addBlockName,
  showBlockTypeSelector = true,
  actionsAddon,
}: ActivityBlockFormListProps) {
  const { isLimitedEdition } = usePedWorkspaceStatus();
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab',
  });
  const { openForms, addForm, removeForm, handleFormSubmit } =
    useFormList<CreateBlockData>({ onSubmit });

  return (
    <div className="flex flex-col gap-6">
      <ConditionalRenderer condition={isLoading}>
        <FieldsetContainer className="flex justify-center items-center py-5">
          <Loading variant="spinner" size="md" color="primary" />
        </FieldsetContainer>
      </ConditionalRenderer>
      {openForms.map(formId => (
        <ActivityBlockInlineForm
          key={formId}
          onSubmit={handleFormSubmit(formId)}
          onCancel={() => removeForm(formId)}
          isLoading={isLoading}
          showBlockTypeSelector={showBlockTypeSelector}
        />
      ))}
      <FieldsetContainer className="flex flex-wrap gap-4">
        <PedWorkspaceGuard className="flex-1">
          <Button
            size="md"
            onClick={addForm}
            className="flex-1 w-full bg-base-300"
            disabled={openForms.length >= 1 || isLimitedEdition}
          >
            <PlusIcon className="w-4 h-4" />
            {t(addBlockName || 'addBlock')}
          </Button>
        </PedWorkspaceGuard>
        {actionsAddon}
      </FieldsetContainer>
    </div>
  );
}
